import React, { useState, useEffect, useRef } from 'react';
import Sidebar from '../../components/Sidebar';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { 
  Send, Image as ImageIcon, 
  Trash2, Plus, RefreshCw, X, Download, Copy, ThumbsUp, Star,
  History, AlertTriangle, Check, Link as LinkIcon,
  Sparkles, Calendar, Building2
} from 'lucide-react';
import confetti from 'canvas-confetti';

const LinkedInIcon = ({ size = 16, className = '' }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"/>
  </svg>
);

interface Message {
  id: string;
  sender: 'user' | 'bot';
  text: string;
  timestamp: string;
  imageUrl?: string;
  isLiked?: boolean;
  isStarred?: boolean;
}

interface ChatSession {
  id: string;
  title: string;
  date: string;
  profileId: string;
  messages: Message[];
}

interface ContentGrid {
  id: string;
  name: string;
  description: string;
  accountId: string;
  googleSheetUrl?: string;
  webhookUrl?: string;
  month?: string;
  createdAt: string;
}

interface ScheduledPost {
  id: string;
  gridId: string;
  accountId: string;
  accountName?: string;
  timezone?: string;
  content: string;
  imageUrl?: string;
  scheduledAt: string;
  status: 'draft' | 'scheduled' | 'published' | 'failed';
  createdAt: string;
}

interface LinkedInAccount {
  urn: string;
  label: string;
  name?: string;
  isCompany?: boolean;
  companyId?: string;
}

interface UserItem {
  id: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  email?: string;
  role?: string;
  avatar?: string;
}

interface DossierConfig {
  id: string;
  name: string;
  role: string;
  tone: string;
  initials: string;
  isCompany?: boolean;
  searchNames: string[];
}

// 5 Perfiles estrictos solicitados
const DOSSIERS: DossierConfig[] = [
  { 
    id: 'acc_1788814251450_jl2q', 
    name: 'Juan Pablo', 
    role: 'UI/UX & Junior Dev', 
    tone: 'Innovador, estético y técnico', 
    initials: 'JP',
    searchNames: ['juan pablo', 'juan'] 
  },
  { 
    id: 'acc_1788814251450_andrea', 
    name: 'Andrea Sosa', 
    role: 'Operations & Cuentas', 
    tone: 'Estratégica, estructurada y ejecutiva', 
    initials: 'AS',
    searchNames: ['andrea sosa', 'andrea'] 
  },
  { 
    id: 'acc_1788814251450_vicente', 
    name: 'Vicente Sosa', 
    role: 'CTO & Innovación', 
    tone: 'Arquitecto tecnológico y visión de producto', 
    initials: 'VS',
    searchNames: ['vicente sosa', 'vicente'] 
  },
  { 
    id: 'acc_1788814251450_nicolas', 
    name: 'Nicolás Fernandez', 
    role: 'CCO & Creativo', 
    tone: 'Narrativa de marca, branding y disrupción', 
    initials: 'NF',
    searchNames: ['nicolas fernandez', 'nicolás fernandez', 'nicolas', 'nicolás'] 
  },
  { 
    id: 'acc_1788814251450_zoik', 
    name: 'Zoik', 
    role: 'Empresa Oficial · Zoik Agency', 
    tone: 'Autoridad, soluciones y cultura corporativa', 
    initials: 'ZK',
    isCompany: true,
    searchNames: ['zoik', 'agency'] 
  }
];

export const LinkedInOpsPage: React.FC = () => {
  const { user } = useAuth();
  const [usersList, setUsersList] = useState<UserItem[]>([]);
  const [selectedDossier, setSelectedDossier] = useState<DossierConfig>(DOSSIERS[0]);
  const [inputMessage, setInputMessage] = useState('');
  const [messages, setMessages] = useState<Record<string, Message[]>>(() => {
    const saved = localStorage.getItem('charge_chat_messages_v2');
    if (saved) {
      try { return JSON.parse(saved); } catch (e) {}
    }
    return {
      [DOSSIERS[0].id]: [{
        id: 'init-1',
        sender: 'bot',
        text: 'Bienvenido al entorno de operaciones de LinkedIn de Zoik Analyst. Selecciona un perfil o redactemos tu próximo post estructurado.',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }]
    };
  });

  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  // Historial y Destacados
  const [sessions, setSessions] = useState<ChatSession[]>(() => {
    const saved = localStorage.getItem('charge_chat_sessions_v2');
    if (saved) {
      try { return JSON.parse(saved); } catch (e) {}
    }
    return [];
  });
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showStarredModal, setShowStarredModal] = useState(false);

  // Parrillas
  const [grids, setGrids] = useState<ContentGrid[]>([]);
  const [selectedGridId, setSelectedGridId] = useState<string>('');
  const [scheduledPosts, setScheduledPosts] = useState<ScheduledPost[]>([]);
  const [showGridModal, setShowGridModal] = useState(false);
  const [newGridName, setNewGridName] = useState('');
  const [newGridDesc, setNewGridDesc] = useState('');
  const [newGridSheet, setNewGridSheet] = useState('');

  // Cuentas LinkedIn
  const [accounts, setAccounts] = useState<Record<string, LinkedInAccount>>({});
  const [showAccountsModal, setShowAccountsModal] = useState(false);
  const [companyPinInput, setCompanyPinInput] = useState('105503301');

  // --- SISTEMA DE MODALES PERSONALIZADOS ---
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmText?: string;
    isDanger?: boolean;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirmar',
    isDanger: false,
    onConfirm: () => {}
  });

  const [promptDialog, setPromptDialog] = useState<{
    isOpen: boolean;
    title: string;
    description: string;
    placeholder: string;
    defaultValue: string;
    onConfirm: (val: string) => void;
  }>({
    isOpen: false,
    title: '',
    description: '',
    placeholder: '',
    defaultValue: '',
    onConfirm: () => {}
  });
  const [promptInputVal, setPromptInputVal] = useState('');

  const [toastNotification, setToastNotification] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'success'
  });

  const showToast = (title: string, message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToastNotification({ isOpen: true, title, message, type });
    setTimeout(() => {
      setToastNotification(prev => ({ ...prev, isOpen: false }));
    }, 4000);
  };

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Cargar usuarios para obtener avatares reales
  useEffect(() => {
    api.get('/users')
      .then(res => {
        if (Array.isArray(res.data)) {
          setUsersList(res.data);
        }
      })
      .catch(() => {});
  }, []);

  // Mapear avatar real según el nombre o perfil
  const getAvatarForDossier = (dossier: DossierConfig): string | null => {
    if (dossier.isCompany) {
      // Intentar encontrar si hay avatar de empresa o usar fallback
      const companyUser = usersList.find(u => {
        const full = `${u.firstName || ''} ${u.lastName || ''} ${u.name || ''}`.toLowerCase();
        return full.includes('zoik');
      });
      return companyUser?.avatar || null;
    }

    const matched = usersList.find(u => {
      const full = `${u.firstName || ''} ${u.lastName || ''} ${u.name || ''}`.toLowerCase().trim();
      return dossier.searchNames.some(sn => full.includes(sn));
    });

    if (matched?.avatar) return matched.avatar;

    // Si el usuario autenticado coincide con este dossier, usar su avatar
    if (user) {
      const authFull = `${user.firstName || ''} ${user.lastName || ''} ${(user as any).name || ''}`.toLowerCase();
      if (dossier.searchNames.some(sn => authFull.includes(sn)) && (user as any).avatar) {
        return (user as any).avatar;
      }
    }

    return null;
  };

  // Guardar en localStorage de forma segura
  useEffect(() => {
    try {
      const sanitized: Record<string, Message[]> = {};
      for (const [key, msgList] of Object.entries(messages)) {
        sanitized[key] = msgList.slice(-50).map(m => ({ ...m, imageUrl: undefined }));
      }
      localStorage.setItem('charge_chat_messages_v2', JSON.stringify(sanitized));
    } catch (e) {}
  }, [messages]);

  useEffect(() => {
    try {
      const sanitizedSessions = sessions.slice(0, 30).map(s => ({
        ...s,
        messages: s.messages.slice(-50).map(m => ({ ...m, imageUrl: undefined }))
      }));
      localStorage.setItem('charge_chat_sessions_v2', JSON.stringify(sanitizedSessions));
    } catch (e) {}
  }, [sessions]);

  // Cargar datos
  const fetchAccounts = async () => {
    try {
      const res = await fetch('/api/linkedin/accounts');
      if (res.ok) setAccounts(await res.json());
    } catch (e) {}
  };

  const fetchGrids = async () => {
    try {
      const res = await fetch('/api/grids');
      if (res.ok) {
        const data = await res.json();
        setGrids(data);
        if (data.length > 0 && !selectedGridId) setSelectedGridId(data[0].id);
      }
    } catch (e) {}
  };

  const fetchPosts = async () => {
    try {
      const url = selectedGridId 
        ? `/api/grid?gridId=${selectedGridId}` 
        : `/api/grid?accountId=${selectedDossier.id}`;
      const res = await fetch(url);
      if (res.ok) setScheduledPosts(await res.json());
    } catch (e) {}
  };

  useEffect(() => {
    fetchAccounts();
    fetchGrids();
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [selectedGridId, selectedDossier]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, selectedDossier]);

  // Nuevo Chat
  const handleNewChat = () => {
    const currentMsgs = messages[selectedDossier.id] || [];
    if (currentMsgs.length > 1) {
      const firstUserMsg = currentMsgs.find(m => m.sender === 'user')?.text || 'Conversación sin título';
      const newSession: ChatSession = {
        id: Date.now().toString(),
        title: firstUserMsg.slice(0, 45) + (firstUserMsg.length > 45 ? '...' : ''),
        date: new Date().toLocaleDateString('es-CO', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }),
        profileId: selectedDossier.id,
        messages: currentMsgs
      };
      setSessions(prev => [newSession, ...prev]);
    }

    setMessages(prev => ({
      ...prev,
      [selectedDossier.id]: [{
        id: Date.now().toString(),
        sender: 'bot',
        text: `Nueva sesión iniciada para ${selectedDossier.name}. ¿Qué contenido deseas estructurar hoy?`,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }]
    }));
  };

  const handleRestoreSession = (session: ChatSession) => {
    setMessages(prev => ({ ...prev, [session.profileId]: session.messages }));
    const targetDossier = DOSSIERS.find(d => d.id === session.profileId) || DOSSIERS[0];
    setSelectedDossier(targetDossier);
    setShowHistoryModal(false);
  };

  // Eliminar sesión del historial con modal propio
  const handleDeleteSession = (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmDialog({
      isOpen: true,
      title: 'Eliminar del Historial',
      message: '¿Estás seguro de que deseas eliminar esta conversación del historial? Esta acción no se puede deshacer.',
      confirmText: 'Eliminar',
      isDanger: true,
      onConfirm: () => {
        setSessions(prev => prev.filter(s => s.id !== sessionId));
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
        showToast('Eliminado', 'Conversación eliminada del historial.');
      }
    });
  };

  // Eliminar publicación de la parrilla con modal propio
  const handleDeletePost = (postId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmDialog({
      isOpen: true,
      title: 'Eliminar Publicación',
      message: '¿Deseas eliminar esta publicación de la parrilla editorial?',
      confirmText: 'Eliminar',
      isDanger: true,
      onConfirm: async () => {
        try {
          const res = await fetch(`/api/grid/${postId}`, { method: 'DELETE' });
          if (res.ok) {
            setScheduledPosts(prev => prev.filter(p => p.id !== postId));
            showToast('Publicación Eliminada', 'Se removió el post de la parrilla.');
          }
        } catch (err: any) {
          showToast('Error', err.message, 'error');
        }
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
      }
    });
  };

  // Desvincular Google Sheet con modal propio
  const handleUnlinkGoogleSheet = () => {
    if (!selectedGridId) return;
    setConfirmDialog({
      isOpen: true,
      title: 'Desvincular Google Sheet',
      message: '¿Deseas desconectar la hoja de Google Sheet de esta parrilla editorial?',
      confirmText: 'Desvincular',
      isDanger: true,
      onConfirm: async () => {
        try {
          const res = await fetch(`/api/grids/${selectedGridId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ googleSheetUrl: '', webhookUrl: '' })
          });
          if (res.ok) {
            fetchGrids();
            showToast('Desvinculado', 'Google Sheet desconectado de la parrilla.');
          }
        } catch (e: any) {
          showToast('Error', e.message, 'error');
        }
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
      }
    });
  };

  // Vincular Google Sheet con modal prompt propio
  const handleLinkGoogleSheet = () => {
    if (!selectedGridId) {
      showToast('Atención', 'Selecciona primero una parrilla específica.', 'info');
      return;
    }
    const current = grids.find(g => g.id === selectedGridId);
    setPromptInputVal(current?.googleSheetUrl || '');
    setPromptDialog({
      isOpen: true,
      title: 'Vincular Google Sheet',
      description: 'Ingresa el enlace de Google Sheet o Webhook Apps Script para sincronizar las publicaciones:',
      placeholder: 'https://docs.google.com/spreadsheets/d/...',
      defaultValue: current?.googleSheetUrl || '',
      onConfirm: async (val: string) => {
        try {
          const res = await fetch(`/api/grids/${selectedGridId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ googleSheetUrl: val })
          });
          if (res.ok) {
            fetchGrids();
            showToast('Éxito', 'Google Sheet vinculado a la parrilla.');
          }
        } catch (e: any) {
          showToast('Error', e.message, 'error');
        }
        setPromptDialog(prev => ({ ...prev, isOpen: false }));
      }
    });
  };

  // Agendar post con modal prompt propio
  const handleSchedulePost = (content: string, imageUrl?: string) => {
    const defaultDate = new Date(Date.now() + 86400000).toISOString().slice(0, 16).replace('T', ' ');
    setPromptInputVal(defaultDate);
    setPromptDialog({
      isOpen: true,
      title: 'Programar en Parrilla',
      description: 'Define la fecha y hora de publicación en formato AAAA-MM-DD HH:MM:',
      placeholder: '2026-09-15 10:00',
      defaultValue: defaultDate,
      onConfirm: async (dateVal: string) => {
        try {
          const res = await fetch('/api/grid', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              gridId: selectedGridId || 'default',
              accountId: selectedDossier.id,
              accountName: selectedDossier.name,
              timezone: 'America/Bogota',
              content: content,
              imageUrl: imageUrl,
              scheduledAt: dateVal,
              status: 'scheduled'
            })
          });

          if (res.ok) {
            showToast('Post Agendado', 'Publicación programada exitosamente en la parrilla.');
            fetchPosts();
          }
        } catch (e: any) {
          showToast('Error al Agendar', e.message, 'error');
        }
        setPromptDialog(prev => ({ ...prev, isOpen: false }));
      }
    });
  };

  // Publicar directamente en LinkedIn
  const handlePublishPost = async (text: string, imageUrl?: string) => {
    const accList = Object.values(accounts);
    const matched = accList.find(a => 
      a.label.toLowerCase().includes(selectedDossier.name.toLowerCase()) ||
      (selectedDossier.name.includes('Zoik') && a.isCompany)
    ) || accList[0];

    if (!matched) {
      showToast('Cuenta Requerida', 'No hay una cuenta de LinkedIn vinculada para este perfil.', 'info');
      setShowAccountsModal(true);
      return;
    }

    try {
      const res = await fetch('/api/linkedin/publish', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accountId: matched.urn,
          text: text,
          imageUrl: imageUrl
        })
      });

      const data = await res.json();
      if (res.ok) {
        confetti({ particleCount: 70, spread: 60, origin: { y: 0.7 } });
        showToast('Publicado con Éxito', `Post publicado en LinkedIn en la cuenta de ${matched.label}.`);
      } else {
        showToast('Error al Publicar', data.error || 'Error desconocido', 'error');
      }
    } catch (e: any) {
      showToast('Error de Conexión', e.message, 'error');
    }
  };

  // Enviar mensaje al chat
  const handleSendMessage = async () => {
    if (!inputMessage.trim() && !selectedImage) return;

    const userMsg: Message = {
      id: Date.now().toString(),
      sender: 'user',
      text: inputMessage || (selectedImage ? 'Analiza esta imagen y redacta un post para LinkedIn' : ''),
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      imageUrl: selectedImage || undefined
    };

    const currentMsgs = messages[selectedDossier.id] || [];
    setMessages({
      ...messages,
      [selectedDossier.id]: [...currentMsgs, userMsg]
    });

    setInputMessage('');
    const targetImg = selectedImage;
    setSelectedImage(null);
    setLoading(true);

    try {
      const res = await fetch('/api/charge/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMsg.text,
          messages: [...currentMsgs, userMsg].map(m => ({ role: m.sender === 'user' ? 'user' : 'assistant', content: m.text, imageUrl: m.imageUrl })),
          targetProfile: selectedDossier.name,
          imageUrl: targetImg
        })
      });

      const data = await res.json();
      const rawText = data.responseText ?? data.reply ?? data.text;
      const textString = typeof rawText === 'object' ? (rawText?.responseText || rawText?.reply || JSON.stringify(rawText)) : String(rawText || 'Sin respuesta del modelo.');

      const botMsg: Message = {
        id: (Date.now() + 1).toString(),
        sender: 'bot',
        text: textString,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        imageUrl: targetImg || undefined
      };

      setMessages(prev => ({
        ...prev,
        [selectedDossier.id]: [...(prev[selectedDossier.id] || []), botMsg]
      }));
      fetchPosts();
    } catch (err: any) {
      const errorMsg: Message = {
        id: (Date.now() + 1).toString(),
        sender: 'bot',
        text: 'Error de comunicación: ' + err.message,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      setMessages(prev => ({
        ...prev,
        [selectedDossier.id]: [...(prev[selectedDossier.id] || []), errorMsg]
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGrid = async () => {
    if (!newGridName.trim()) return;
    try {
      const res = await fetch('/api/grids', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newGridName,
          description: newGridDesc,
          accountId: selectedDossier.id,
          googleSheetUrl: newGridSheet
        })
      });
      if (res.ok) {
        setNewGridName('');
        setNewGridDesc('');
        setNewGridSheet('');
        setShowGridModal(false);
        fetchGrids();
        showToast('Parrilla Creada', 'La nueva parrilla se configuró con éxito.');
      }
    } catch (e: any) {
      showToast('Error', e.message, 'error');
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => setSelectedImage(event.target?.result as string);
      reader.readAsDataURL(file);
    }
  };

  const starredMessages = (messages[selectedDossier.id] || []).filter(m => m.isStarred);
  const activeAvatar = getAvatarForDossier(selectedDossier);

  return (
    <div className="zoik-page-container" style={{
      display: 'flex',
      height: '100vh',
      maxHeight: '100vh',
      width: '100vw',
      maxWidth: '100vw',
      overflow: 'hidden',
      background: 'radial-gradient(at 0% 0%, rgba(0, 112, 243, 0.07) 0px, transparent 50%), radial-gradient(at 100% 0%, rgba(38, 136, 201, 0.08) 0px, transparent 50%), radial-gradient(at 100% 100%, rgba(99, 102, 241, 0.06) 0px, transparent 50%), radial-gradient(at 0% 100%, rgba(16, 185, 129, 0.05) 0px, transparent 50%), #edf2f8',
      fontFamily: '"Montserrat", sans-serif'
    }}>
      {/* SIDEBAR ESTÁTICO */}
      <Sidebar />

      {/* CONTENIDO PRINCIPAL CON SCROLL INTERNO */}
      <main className="zoik-page-main" style={{
        flex: 1,
        height: '100vh',
        maxHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        overflowX: 'hidden',
        minWidth: 0,
        padding: '28px 36px',
        boxSizing: 'border-box'
      }}>
        {/* BANNER HEADER GLASSMORPHISM */}
        <div style={{
          background: 'linear-gradient(135deg, #0f2a4a 0%, #173d6b 100%)',
          borderRadius: '24px',
          padding: '24px 32px',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '14px',
          marginBottom: '22px',
          boxShadow: '0 12px 32px rgba(15, 42, 74, 0.16)',
          border: '1px solid rgba(255, 255, 255, 0.12)',
          backdropFilter: 'blur(16px)',
          flexShrink: 0
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '6px' }}>
              <div style={{
                background: 'rgba(38, 136, 201, 0.25)',
                border: '1px solid rgba(38, 136, 201, 0.5)',
                padding: '5px 12px',
                borderRadius: '8px',
                fontSize: '11px',
                fontWeight: 800,
                letterSpacing: '1px',
                color: '#60a5fa',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <Sparkles size={13} />
                CHARGE OPS
              </div>
              <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 800, letterSpacing: '-0.5px' }}>
                LinkedIn Operations &amp; IA Multi-Perfil
              </h1>
            </div>
            <p style={{ margin: 0, fontSize: '13.5px', color: 'rgba(255, 255, 255, 0.75)' }}>
              Estructuración de posts con voz personalizada, análisis multimodal de imágenes y sincronización editorial.
            </p>
          </div>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={handleNewChat}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                background: 'rgba(255, 255, 255, 0.12)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: 'white', padding: '9px 15px', borderRadius: '12px',
                fontSize: '12.5px', fontWeight: 700, cursor: 'pointer',
                backdropFilter: 'blur(8px)',
                transition: 'all 0.2s'
              }}
            >
              <Plus size={15} /> Nuevo Chat
            </button>
            <button
              onClick={() => setShowHistoryModal(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                background: 'rgba(255, 255, 255, 0.08)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                color: 'white', padding: '9px 14px', borderRadius: '12px',
                fontSize: '12.5px', fontWeight: 600, cursor: 'pointer',
                backdropFilter: 'blur(8px)'
              }}
            >
              <History size={15} /> Historial ({sessions.length})
            </button>
            <button
              onClick={() => setShowStarredModal(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                background: 'rgba(255, 255, 255, 0.08)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                color: 'white', padding: '9px 14px', borderRadius: '12px',
                fontSize: '12.5px', fontWeight: 600, cursor: 'pointer',
                backdropFilter: 'blur(8px)'
              }}
            >
              <Star size={15} /> Destacados ({starredMessages.length})
            </button>
            <button
              onClick={() => setShowAccountsModal(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                background: '#0a66c2',
                border: 'none',
                color: 'white', padding: '9px 15px', borderRadius: '12px',
                fontSize: '12.5px', fontWeight: 700, cursor: 'pointer',
                boxShadow: 'none'
              }}
            >
              <LinkedInIcon size={15} /> Cuentas ({Object.keys(accounts).length})
            </button>
            <button
              onClick={() => window.open(`/api/grid/export/excel?gridId=${selectedGridId || ''}`, '_blank')}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                background: '#2688c9',
                border: 'none',
                color: 'white', padding: '9px 15px', borderRadius: '12px',
                fontSize: '12.5px', fontWeight: 700, cursor: 'pointer',
                boxShadow: 'none'
              }}
            >
              <Download size={15} /> Excel
            </button>
          </div>
        </div>

        {/* TABS DE PERFILES SELECCIONADOS (5 EXCLUSIVOS CON FOTO DE PERFIL) */}
        <div className="zoik-touch-scroll" style={{
          display: 'flex',
          gap: '12px',
          overflowX: 'auto',
          paddingBottom: '6px',
          marginBottom: '22px',
          flexShrink: 0
        }}>
          {DOSSIERS.map(dossier => {
            const isSelected = selectedDossier.id === dossier.id;
            const avatarUrl = getAvatarForDossier(dossier);

            return (
              <button
                key={dossier.id}
                onClick={() => setSelectedDossier(dossier)}
                style={{
                  background: isSelected 
                    ? 'rgba(255, 255, 255, 0.95)' 
                    : 'rgba(255, 255, 255, 0.55)',
                  border: isSelected 
                    ? '1.5px solid rgba(0, 112, 243, 0.6)' 
                    : '1px solid rgba(255, 255, 255, 0.7)',
                  borderRadius: '18px',
                  padding: '12px 14px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  boxShadow: isSelected 
                    ? '0 10px 25px rgba(0, 112, 243, 0.12)' 
                    : '0 4px 16px rgba(15, 42, 74, 0.03)',
                  backdropFilter: 'blur(16px)',
                  transition: 'all 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {isSelected && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '3px',
                    background: 'linear-gradient(90deg, #0070f3, #2688c9)'
                  }} />
                )}

                {/* FOTO O AVATAR ESTILIZADO */}
                <div style={{
                  width: '42px',
                  height: '42px',
                  borderRadius: '14px',
                  overflow: 'hidden',
                  flexShrink: 0,
                  background: isSelected ? 'linear-gradient(135deg, #0070f3, #0f2a4a)' : '#e2e8f0',
                  color: isSelected ? 'white' : '#475569',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '13px',
                  fontWeight: 800,
                  border: isSelected ? '2px solid rgba(0, 112, 243, 0.3)' : '1px solid rgba(0, 0, 0, 0.05)',
                  boxShadow: isSelected ? '0 4px 12px rgba(0, 112, 243, 0.2)' : 'none'
                }}>
                  {avatarUrl ? (
                    <img 
                      src={avatarUrl} 
                      alt={dossier.name} 
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                      onError={(e) => {
                        // Fallback si la imagen no carga
                        (e.target as HTMLElement).style.display = 'none';
                      }}
                    />
                  ) : dossier.isCompany ? (
                    <Building2 size={20} color={isSelected ? 'white' : '#0f2a4a'} />
                  ) : (
                    <span>{dossier.initials}</span>
                  )}
                </div>

                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{
                    fontSize: '13.5px',
                    fontWeight: 800,
                    color: isSelected ? '#0f2a4a' : '#1e293b',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {dossier.name}
                  </div>
                  <div style={{
                    fontSize: '11px',
                    color: isSelected ? '#0070f3' : '#64748b',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {dossier.role}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* PANEL PRINCIPAL: CHAT IA & PARRILLA EDITORIAL */}
        <div className="dash-section-grid-2" style={{
          display: 'grid',
          gridTemplateColumns: '1.4fr 1fr',
          gap: '24px',
          flex: 1,
          minHeight: '620px',
          alignItems: 'stretch'
        }}>
          {/* PANEL IZQUIERDO: CHAT CON GPT-4O */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.72)',
            borderRadius: '24px',
            border: '1px solid rgba(255, 255, 255, 0.85)',
            boxShadow: '0 12px 36px rgba(15, 42, 74, 0.05)',
            backdropFilter: 'blur(20px)',
            display: 'flex',
            flexDirection: 'column',
            height: '620px',
            overflow: 'hidden'
          }}>
            {/* CABECERA DEL CHAT */}
            <div style={{
              padding: '16px 22px',
              borderBottom: '1px solid rgba(226, 232, 240, 0.6)',
              background: 'rgba(255, 255, 255, 0.5)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '10px',
                  overflow: 'hidden',
                  background: '#0f2a4a',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '11px',
                  fontWeight: 800
                }}>
                  {activeAvatar ? (
                    <img src={activeAvatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span>{selectedDossier.initials}</span>
                  )}
                </div>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 800, color: '#0f2a4a' }}>
                    {selectedDossier.name}
                  </div>
                  <div style={{ fontSize: '11px', color: '#64748b' }}>
                    {selectedDossier.tone}
                  </div>
                </div>
              </div>

              <div style={{
                fontSize: '11px',
                background: 'rgba(224, 242, 254, 0.8)',
                color: '#0369a1',
                padding: '4px 10px',
                borderRadius: '8px',
                fontWeight: 700,
                border: '1px solid rgba(186, 230, 253, 0.8)',
                display: 'flex',
                alignItems: 'center',
                gap: '5px'
              }}>
                <Sparkles size={12} />
                GPT-4o Vision Activo
              </div>
            </div>

            {/* MENSAJES DEL CHAT */}
            <div style={{
              flex: 1,
              padding: '20px 22px',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}>
              {(messages[selectedDossier.id] || []).map((msg) => (
                <div
                  key={msg.id}
                  style={{
                    alignSelf: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                    maxWidth: '85%',
                    background: msg.sender === 'user' 
                      ? '#0f2a4a' 
                      : 'rgba(255, 255, 255, 0.92)',
                    color: msg.sender === 'user' ? '#ffffff' : '#0f172a',
                    borderRadius: msg.sender === 'user' ? '20px 20px 4px 20px' : '20px 20px 20px 4px',
                    padding: '16px 20px',
                    border: msg.sender === 'user' ? 'none' : '1px solid rgba(226, 232, 240, 0.85)',
                    boxShadow: msg.sender === 'user' 
                      ? '0 6px 18px rgba(15, 42, 74, 0.15)' 
                      : '0 4px 16px rgba(0, 0, 0, 0.03)',
                    backdropFilter: 'blur(12px)'
                  }}
                >
                  {msg.imageUrl && (
                    <img 
                      src={msg.imageUrl} 
                      alt="Adjunto" 
                      style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '12px', marginBottom: '12px', border: '1px solid rgba(255,255,255,0.2)' }} 
                    />
                  )}
                  <div style={{ whiteSpace: 'pre-wrap', fontSize: '13.5px', lineHeight: '1.6', fontWeight: 500 }}>
                    {typeof msg.text === 'object' ? ((msg.text as any)?.responseText || JSON.stringify(msg.text)) : String(msg.text || '')}
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: '12px',
                    fontSize: '11px',
                    opacity: msg.sender === 'user' ? 0.75 : 0.85,
                    borderTop: msg.sender === 'bot' ? '1px solid rgba(241, 245, 249, 0.8)' : 'none',
                    paddingTop: msg.sender === 'bot' ? '10px' : '0'
                  }}>
                    <span>{msg.timestamp}</span>
                    {msg.sender === 'bot' && (
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <button
                          onClick={() => {
                            setMessages(prev => {
                              const list = (prev[selectedDossier.id] || []).map(m => m.id === msg.id ? { ...m, isLiked: !m.isLiked } : m);
                              return { ...prev, [selectedDossier.id]: list };
                            });
                          }}
                          title="Me gusta"
                          style={{
                            background: 'rgba(241, 245, 249, 0.8)',
                            border: '1px solid #e2e8f0',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            color: msg.isLiked ? '#2563eb' : '#64748b',
                            padding: '4px 6px',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <ThumbsUp size={13} />
                        </button>
                        <button
                          onClick={() => {
                            setMessages(prev => {
                              const list = (prev[selectedDossier.id] || []).map(m => m.id === msg.id ? { ...m, isStarred: !m.isStarred } : m);
                              return { ...prev, [selectedDossier.id]: list };
                            });
                            showToast('Destacados', msg.isStarred ? 'Removido de destacados' : 'Guardado en destacados.');
                          }}
                          title="Destacar post"
                          style={{
                            background: 'rgba(241, 245, 249, 0.8)',
                            border: '1px solid #e2e8f0',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            color: msg.isStarred ? '#eab308' : '#64748b',
                            padding: '4px 6px',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <Star size={13} />
                        </button>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(msg.text);
                            showToast('Copiado', 'Texto copiado al portapapeles.');
                          }}
                          title="Copiar texto"
                          style={{
                            background: 'rgba(241, 245, 249, 0.8)',
                            border: '1px solid #e2e8f0',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            color: '#64748b',
                            padding: '4px 6px',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <Copy size={13} />
                        </button>
                        <button
                          onClick={() => handlePublishPost(msg.text, msg.imageUrl)}
                          style={{
                            background: '#0a66c2',
                            color: 'white',
                            border: 'none',
                            padding: '5px 10px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '11px',
                            fontWeight: 700,
                            boxShadow: 'none'
                          }}
                        >
                          Publicar
                        </button>
                        <button
                          onClick={() => handleSchedulePost(msg.text, msg.imageUrl)}
                          style={{
                            background: '#0f2a4a',
                            color: 'white',
                            border: 'none',
                            padding: '5px 10px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '11px',
                            fontWeight: 700,
                            boxShadow: 'none'
                          }}
                        >
                          Agendar
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {loading && (
                <div style={{
                  alignSelf: 'flex-start',
                  color: '#0f2a4a',
                  background: 'rgba(255, 255, 255, 0.85)',
                  padding: '12px 18px',
                  borderRadius: '16px',
                  border: '1px solid #e2e8f0',
                  fontSize: '12.5px',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <RefreshCw size={14} className="animate-spin" />
                  Redactando post estructurado para {selectedDossier.name}...
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* INPUT FLOTANTE CON GLASSMORPHISM */}
            <div style={{
              padding: '16px 20px',
              borderTop: '1px solid rgba(226, 232, 240, 0.6)',
              background: 'rgba(255, 255, 255, 0.75)',
              backdropFilter: 'blur(16px)'
            }}>
              {selectedImage && (
                <div style={{ position: 'relative', display: 'inline-block', marginBottom: '10px' }}>
                  <img src={selectedImage} alt="Preview" style={{ height: '60px', borderRadius: '10px', border: '1px solid #cbd5e1' }} />
                  <button
                    onClick={() => setSelectedImage(null)}
                    style={{
                      position: 'absolute',
                      top: -6,
                      right: -6,
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: '20px',
                      height: '20px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    ×
                  </button>
                </div>
              )}
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleImageUpload}
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  title="Subir imagen para análisis multimodal"
                  style={{
                    background: 'rgba(241, 245, 249, 0.9)',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    padding: '11px',
                    cursor: 'pointer',
                    color: '#0f2a4a',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <ImageIcon size={18} />
                </button>
                <input
                  type="text"
                  placeholder={`Escribe una idea o tema para ${selectedDossier.name}...`}
                  value={inputMessage}
                  onChange={e => setInputMessage(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSendMessage()}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    borderRadius: '12px',
                    border: '1px solid rgba(203, 213, 225, 0.8)',
                    fontSize: '13.5px',
                    outline: 'none',
                    background: 'rgba(255, 255, 255, 0.9)',
                    color: '#0f172a',
                    fontFamily: '"Montserrat", sans-serif'
                  }}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={loading}
                  style={{
                    background: '#0f2a4a',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    padding: '12px 20px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: 'none',
                    fontFamily: '"Montserrat", sans-serif',
                    fontSize: '13px'
                  }}
                >
                  <Send size={15} /> Enviar
                </button>
              </div>
            </div>
          </div>

          {/* PANEL DERECHO: PARRILLA EDITORIAL */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.72)',
            borderRadius: '24px',
            border: '1px solid rgba(255, 255, 255, 0.85)',
            boxShadow: '0 12px 36px rgba(15, 42, 74, 0.05)',
            backdropFilter: 'blur(20px)',
            display: 'flex',
            flexDirection: 'column',
            height: '620px',
            padding: '22px',
            boxSizing: 'border-box'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 800, color: '#0f2a4a' }}>
                  Parrilla Editorial
                </h2>
                <div style={{ fontSize: '12px', color: '#64748b' }}>
                  {scheduledPosts.length} publicaciones programadas
                </div>
              </div>

              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                {(() => {
                  const activeGrid = grids.find(g => g.id === selectedGridId);
                  const hasSheet = !!activeGrid?.googleSheetUrl;
                  return (
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                      <button
                        onClick={() => hasSheet ? window.open(activeGrid.googleSheetUrl, '_blank') : handleLinkGoogleSheet()}
                        title={hasSheet ? "Abrir Google Sheet en nueva pestaña" : "Vincular Google Sheet"}
                        style={{
                          background: hasSheet ? 'rgba(16, 185, 129, 0.12)' : 'rgba(241, 245, 249, 0.8)',
                          color: hasSheet ? '#047857' : '#0f2a4a',
                          border: hasSheet ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid #e2e8f0',
                          padding: '6px 10px',
                          borderRadius: '8px',
                          fontSize: '11px',
                          fontWeight: 700,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        Sheet {hasSheet ? '↗' : '+'}
                      </button>
                      {hasSheet && (
                        <button
                          onClick={handleUnlinkGoogleSheet}
                          title="Desvincular Google Sheet"
                          style={{
                            background: '#fee2e2',
                            color: '#b91c1c',
                            border: '1px solid #fecaca',
                            padding: '6px 8px',
                            borderRadius: '8px',
                            fontSize: '11px',
                            fontWeight: 700,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  );
                })()}

                <button
                  onClick={() => setShowGridModal(true)}
                  style={{
                    background: '#0f2a4a',
                    border: 'none',
                    color: 'white',
                    padding: '6px 12px',
                    borderRadius: '8px',
                    fontSize: '11.5px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <Plus size={13} /> Nueva
                </button>
              </div>
            </div>

            {/* SELECTOR DE PARRILLA */}
            <select
              value={selectedGridId}
              onChange={e => setSelectedGridId(e.target.value)}
              style={{
                padding: '10px 14px',
                borderRadius: '12px',
                border: '1px solid #cbd5e1',
                fontSize: '13px',
                marginBottom: '16px',
                outline: 'none',
                background: 'rgba(255, 255, 255, 0.85)',
                color: '#0f2a4a',
                fontFamily: '"Montserrat", sans-serif'
              }}
            >
              <option value="">Todas las parrillas</option>
              {grids.map(g => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>

            {/* LISTA DE POSTS PROGRAMADOS */}
            <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px', paddingRight: '4px' }}>
              {scheduledPosts.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  color: '#94a3b8',
                  fontSize: '13px',
                  marginTop: '80px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Calendar size={32} strokeWidth={1.5} color="#cbd5e1" />
                  No hay publicaciones agendadas en esta parrilla.
                </div>
              ) : (
                scheduledPosts.map(post => (
                  <div
                    key={post.id}
                    style={{
                      border: '1px solid rgba(226, 232, 240, 0.9)',
                      borderRadius: '14px',
                      padding: '14px',
                      background: 'rgba(255, 255, 255, 0.85)',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.02)'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{ fontSize: '11.5px', fontWeight: 800, color: '#0070f3' }}>
                        {post.scheduledAt}
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{
                          fontSize: '10px',
                          background: post.status === 'published' ? '#dcfce7' : '#fef3c7',
                          color: post.status === 'published' ? '#166534' : '#92400e',
                          padding: '3px 8px',
                          borderRadius: '6px',
                          fontWeight: 700
                        }}>
                          {post.status.toUpperCase()}
                        </span>
                        <button
                          onClick={(e) => handleDeletePost(post.id, e)}
                          title="Eliminar publicación"
                          style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center' }}
                          onMouseEnter={(e) => (e.currentTarget.style.color = '#ef4444')}
                          onMouseLeave={(e) => (e.currentTarget.style.color = '#94a3b8')}
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                    {post.imageUrl && (
                      <div style={{ width: '100%', height: '110px', borderRadius: '8px', overflow: 'hidden', marginBottom: '8px', background: '#f8fafc', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <img src={post.imageUrl} alt="Adjunto" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                    )}
                    <div style={{ fontSize: '12.5px', color: '#334155', maxHeight: '60px', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', lineHeight: '1.5' }}>
                      {post.content}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* --- MODALES PERSONALIZADOS LIQUID GLASSMORPHISM --- */}

        {/* 1. MODAL DE CONFIRMACIÓN */}
        {confirmDialog.isOpen && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.45)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
            <div style={{ background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(24px)', borderRadius: '24px', padding: '28px', width: '420px', boxShadow: '0 25px 50px -12px rgba(15, 42, 74, 0.25)', border: '1px solid rgba(255, 255, 255, 0.9)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: confirmDialog.isDanger ? '#fee2e2' : '#e0f2fe', color: confirmDialog.isDanger ? '#ef4444' : '#0284c7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <AlertTriangle size={20} />
                </div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>{confirmDialog.title}</h3>
              </div>
              <p style={{ margin: '0 0 24px 0', fontSize: '13.5px', color: '#475569', lineHeight: '1.6' }}>{confirmDialog.message}</p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button
                  onClick={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
                  style={{ background: '#f1f5f9', color: '#475569', border: 'none', padding: '10px 18px', borderRadius: '12px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmDialog.onConfirm}
                  style={{ background: confirmDialog.isDanger ? '#ef4444' : '#0f2a4a', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '12px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}
                >
                  {confirmDialog.confirmText || 'Confirmar'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 2. MODAL PROMPT / INPUT */}
        {promptDialog.isOpen && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.45)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
            <div style={{ background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(24px)', borderRadius: '24px', padding: '28px', width: '460px', boxShadow: '0 25px 50px -12px rgba(15, 42, 74, 0.25)', border: '1px solid rgba(255, 255, 255, 0.9)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: '#e0f2fe', color: '#0284c7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <LinkIcon size={20} />
                </div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>{promptDialog.title}</h3>
              </div>
              <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#64748b' }}>{promptDialog.description}</p>
              <input
                type="text"
                placeholder={promptDialog.placeholder}
                value={promptInputVal}
                onChange={e => setPromptInputVal(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && promptDialog.onConfirm(promptInputVal)}
                autoFocus
                style={{ width: '100%', padding: '12px 16px', borderRadius: '12px', border: '1px solid #cbd5e1', fontSize: '13.5px', marginBottom: '22px', boxSizing: 'border-box', outline: 'none' }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button
                  onClick={() => setPromptDialog(prev => ({ ...prev, isOpen: false }))}
                  style={{ background: '#f1f5f9', color: '#475569', border: 'none', padding: '10px 18px', borderRadius: '12px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                >
                  Cancelar
                </button>
                <button
                  onClick={() => promptDialog.onConfirm(promptInputVal)}
                  style={{ background: '#0f2a4a', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '12px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}
                >
                  Guardar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 3. TOAST NOTIFICACIÓN FLOTANTE ESTILO APPLE */}
        {toastNotification.isOpen && (
          <div style={{
            position: 'fixed',
            top: '24px',
            right: '24px',
            background: 'rgba(255, 255, 255, 0.86)',
            backdropFilter: 'blur(30px) saturate(190%)',
            WebkitBackdropFilter: 'blur(30px) saturate(190%)',
            color: '#0f2a4a',
            padding: '12px 16px',
            borderRadius: '20px',
            boxShadow: '0 16px 36px -6px rgba(15, 42, 74, 0.16), 0 4px 14px rgba(15, 42, 74, 0.06), inset 0 1px 1.5px rgba(255, 255, 255, 0.98)',
            display: 'flex',
            flexDirection: 'column',
            gap: '5px',
            zIndex: 9999,
            border: '1px solid rgba(255, 255, 255, 0.95)',
            minWidth: '280px',
            maxWidth: '380px',
            animation: 'appleToastEnter 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards'
          }}>
            {/* Apple Notification Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '5px',
                  background: 'linear-gradient(135deg, #0070f3, #0f2a4a)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '9px',
                  fontWeight: 900,
                  boxShadow: '0 2px 4px rgba(0, 112, 243, 0.3)'
                }}>
                  Z
                </div>
                <span style={{ fontSize: '10px', fontWeight: 800, letterSpacing: '0.5px', color: 'rgba(15, 42, 74, 0.5)', textTransform: 'uppercase' }}>
                  Zoik Analyst
                </span>
              </div>
              <span style={{ fontSize: '9.5px', fontWeight: 600, color: 'rgba(15, 42, 74, 0.38)' }}>
                ahora
              </span>
            </div>

            {/* Apple Notification Content */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '2px' }}>
              <div style={{
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                background: toastNotification.type === 'error' ? '#ff3b30' : '#34c759',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <Check size={12} strokeWidth={3} />
              </div>
              <div>
                <div style={{ fontSize: '12.5px', fontWeight: 700, color: '#0f2a4a', lineHeight: '1.3' }}>{toastNotification.title}</div>
                <div style={{ fontSize: '11.5px', color: 'rgba(15, 42, 74, 0.75)', lineHeight: '1.4', marginTop: '1px' }}>{toastNotification.message}</div>
              </div>
            </div>
          </div>
        )}

        {/* MODAL HISTORIAL */}
        {showHistoryModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.45)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(24px)', borderRadius: '24px', padding: '28px', width: '560px', maxHeight: '80vh', overflowY: 'auto', border: '1px solid rgba(255, 255, 255, 0.9)', boxShadow: '0 25px 50px -12px rgba(15, 42, 74, 0.25)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#0f2a4a' }}>Historial de Conversaciones</h3>
                <button onClick={() => setShowHistoryModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}><X size={20} /></button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {sessions.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#94a3b8', padding: '24px' }}>No hay sesiones archivadas.</div>
                ) : (
                  sessions.map(s => (
                    <div
                      key={s.id}
                      onClick={() => handleRestoreSession(s)}
                      style={{ padding: '14px 18px', background: 'rgba(248, 250, 252, 0.8)', border: '1px solid #e2e8f0', borderRadius: '14px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    >
                      <div style={{ flex: 1, paddingRight: '12px' }}>
                        <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>{s.title}</div>
                        <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>{s.date} • {s.messages.length} mensajes</div>
                      </div>
                      <button
                        onClick={(e) => handleDeleteSession(s.id, e)}
                        title="Eliminar del historial"
                        style={{ background: 'none', border: 'none', color: '#94a3b8', padding: '6px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = '#ef4444')}
                        onMouseLeave={(e) => (e.currentTarget.style.color = '#94a3b8')}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* MODAL DESTACADOS */}
        {showStarredModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.45)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(24px)', borderRadius: '24px', padding: '28px', width: '560px', maxHeight: '80vh', overflowY: 'auto', border: '1px solid rgba(255, 255, 255, 0.9)', boxShadow: '0 25px 50px -12px rgba(15, 42, 74, 0.25)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#0f2a4a' }}>Posts Destacados ({selectedDossier.name})</h3>
                <button onClick={() => setShowStarredModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}><X size={20} /></button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {starredMessages.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#94a3b8', padding: '24px' }}>No has marcado ningún post como destacado en este perfil.</div>
                ) : (
                  starredMessages.map(m => (
                    <div key={m.id} style={{ padding: '14px 18px', background: '#fefce8', border: '1px solid #fef08a', borderRadius: '14px' }}>
                      <div style={{ fontSize: '13px', color: '#1e293b', whiteSpace: 'pre-wrap' }}>{m.text}</div>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}>
                        <button onClick={() => { navigator.clipboard.writeText(m.text); showToast('Copiado', 'Texto copiado.'); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}><Copy size={13} /></button>
                        <button onClick={() => handlePublishPost(m.text, m.imageUrl)} style={{ background: '#0a66c2', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: 600 }}>Publicar</button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* MODAL CREAR PARRILLA */}
        {showGridModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.45)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(24px)', borderRadius: '24px', padding: '28px', width: '440px', boxShadow: '0 25px 50px -12px rgba(15, 42, 74, 0.25)', border: '1px solid rgba(255, 255, 255, 0.9)' }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: 800, color: '#0f2a4a' }}>Crear Nueva Parrilla</h3>
              <input
                type="text"
                placeholder="Nombre de la parrilla (ej. Lanzamiento Septiembre)"
                value={newGridName}
                onChange={e => setNewGridName(e.target.value)}
                style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #cbd5e1', marginBottom: '12px', boxSizing: 'border-box' }}
              />
              <input
                type="text"
                placeholder="Enlace Google Sheet (Opcional)"
                value={newGridSheet}
                onChange={e => setNewGridSheet(e.target.value)}
                style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #cbd5e1', marginBottom: '12px', boxSizing: 'border-box' }}
              />
              <textarea
                placeholder="Descripción o temática de la parrilla"
                value={newGridDesc}
                onChange={e => setNewGridDesc(e.target.value)}
                style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #cbd5e1', marginBottom: '18px', boxSizing: 'border-box', minHeight: '70px', fontFamily: '"Montserrat", sans-serif' }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button onClick={() => setShowGridModal(false)} style={{ background: '#f1f5f9', border: 'none', padding: '10px 18px', borderRadius: '12px', cursor: 'pointer', fontWeight: 600 }}>Cancelar</button>
                <button onClick={handleCreateGrid} style={{ background: '#0f2a4a', color: 'white', border: 'none', padding: '10px 18px', borderRadius: '12px', cursor: 'pointer', fontWeight: 700 }}>Guardar</button>
              </div>
            </div>
          </div>
        )}

        {/* MODAL GESTOR DE CUENTAS */}
        {showAccountsModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.45)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(24px)', borderRadius: '24px', padding: '28px', width: '520px', maxHeight: '80vh', overflowY: 'auto', border: '1px solid rgba(255, 255, 255, 0.9)', boxShadow: '0 25px 50px -12px rgba(15, 42, 74, 0.25)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#0f2a4a' }}>Cuentas de LinkedIn Conectadas</h3>
                <button onClick={() => setShowAccountsModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}><X size={20} /></button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '24px' }}>
                {Object.keys(accounts).length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#94a3b8', padding: '12px' }}>No hay cuentas vinculadas actualmente.</div>
                ) : (
                  Object.entries(accounts).map(([key, acc]) => (
                    <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: 'rgba(248, 250, 252, 0.8)', borderRadius: '14px', border: '1px solid #e2e8f0' }}>
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>{acc.label}</div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>{acc.urn}</div>
                      </div>
                      <span style={{ fontSize: '11px', color: '#166534', background: '#dcfce7', padding: '4px 8px', borderRadius: '6px', fontWeight: 600 }}>Conectado</span>
                    </div>
                  ))
                )}
              </div>

              <div style={{ padding: '16px', background: 'rgba(240, 253, 244, 0.8)', borderRadius: '14px', border: '1px solid #bbf7d0', marginBottom: '16px' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: '#166534', marginBottom: '8px' }}>Vincular Perfil Personal</div>
                <button
                  onClick={async () => {
                    const res = await fetch(`/api/linkedin/auth-url?label=${encodeURIComponent(selectedDossier.name)}`);
                    const data = await res.json();
                    if (data.url) window.location.href = data.url;
                  }}
                  style={{ background: '#0a66c2', color: 'white', border: 'none', padding: '10px 16px', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}
                >
                  Conectar con LinkedIn
                </button>
              </div>

              <div style={{ padding: '16px', background: 'rgba(239, 246, 255, 0.8)', borderRadius: '14px', border: '1px solid #bfdbfe' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e40af', marginBottom: '8px' }}>Vincular Página de Empresa</div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    value={companyPinInput}
                    onChange={e => setCompanyPinInput(e.target.value)}
                    placeholder="PIN / ID Organización"
                    style={{ flex: 1, padding: '8px 12px', borderRadius: '10px', border: '1px solid #cbd5e1' }}
                  />
                  <button
                    onClick={async () => {
                      const res = await fetch(`/api/linkedin/auth-url?label=Zoik+Agency+SAS&companyId=${companyPinInput}`);
                      const data = await res.json();
                      if (data.url) window.location.href = data.url;
                    }}
                    style={{ background: '#0f2a4a', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}
                  >
                    Vincular PIN
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default LinkedInOpsPage;
